import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import {
  RiAddLargeLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiPencilLine,
  RiSearchLine,
  RiUploadLine,
} from "@remixicon/react";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuLoaderCircle, LuTriangleAlert } from "react-icons/lu";
import type { Moodboard, MoodboardFile } from "../../types";
import {
  addImagesToMoodboard,
  createMoodboard,
  deleteMoodboard,
  removeImageFromMoodboard,
  updateMoodboardName,
} from "../../apiService";
import Lottie from "lottie-react";
import uploadingLoader from "@/assets/uploading_loader.json";
import EmptySVG from "@/components/hoc/mediaLibrary/empty.svg";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  moodboards: Moodboard[];
  selectedMoodboardId: string | null;
  initialActiveMoodboardId?: string | null;
  onSelectMoodboardId: (id: string | null) => void;
  onMoodboardsChange: (updater: (prev: Moodboard[]) => Moodboard[]) => void;
}

const DEFAULT_NEW_NAME = "My Moodboard";

const shouldShowFallbackToast = (error: unknown) => {
  // The global axios interceptor already shows API error toasts (including `error_object`).
  // Only show a local fallback toast for true network/no-response errors.
  if (isAxiosError(error)) {
    const code = (error as { code?: string }).code;
    if (code === "ERR_CANCELED" || error.name === "CanceledError") return false;
    return !error.response;
  }
  return true;
};

type UploadingItem = {
  id: string;
  file: File;
  preview: string;
};

const MoodboardManagerDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  accountId,
  moodboards,
  selectedMoodboardId,
  initialActiveMoodboardId = null,
  onSelectMoodboardId,
  onMoodboardsChange,
}) => {
  // IMPORTANT: The moodboard "active" in this dialog is for managing (viewing images/renaming/deleting),
  // and should NOT change the "selected style" used in the Create input while browsing the modal.
  const [activeMoodboardId, setActiveMoodboardId] = useState<string | null>(null);

  const activeMoodboard = useMemo(
    () => (activeMoodboardId ? moodboards.find((m) => m.id === activeMoodboardId) : null),
    [moodboards, activeMoodboardId],
  );

  // If the active moodboard id no longer exists (e.g. optimistic id replaced by real id),
  // snap to the selected style id (or first available) without re-initializing on every list update.
  useEffect(() => {
    if (!open) return;
    if (!activeMoodboardId) return;
    const exists = moodboards.some((m) => m.id === activeMoodboardId);
    if (exists) return;
    setActiveMoodboardId(selectedMoodboardId ?? moodboards[0]?.id ?? null);
  }, [open, activeMoodboardId, moodboards, selectedMoodboardId]);

  // Ensure a selection exists when opening (if any moodboards exist)
  const didInitActiveOnOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      didInitActiveOnOpenRef.current = false;
      return;
    }
    // Only initialize once per open so renames/uploads (which update `moodboards`) don't reset selection.
    if (didInitActiveOnOpenRef.current) return;
    didInitActiveOnOpenRef.current = true;
    setActiveMoodboardId(
      initialActiveMoodboardId ?? selectedMoodboardId ?? moodboards[0]?.id ?? null,
    );
  }, [open, initialActiveMoodboardId, selectedMoodboardId, moodboards]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingItemsByMoodboardId, setUploadingItemsByMoodboardId] = useState<
    Record<string, UploadingItem[]>
  >({});
  // Pin the intended upload target at click-time to avoid races if activeMoodboardId changes
  // between opening the file picker and confirming the selection.
  const uploadTargetMoodboardIdRef = useRef<string | null>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const chipsScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Count of in-flight creates (allows multiple concurrent creates; UI doesn't need to render it)
  const [, setCreatingMoodboardCount] = useState(0);
  // Track "where to go back to" per optimistic moodboard so concurrent creates don't clobber each other.
  const createFallbackActiveIdByTempIdRef = useRef<Record<string, string | null>>({});
  const didAutoStartEditForOptimisticIdRef = useRef<Record<string, boolean>>({});
  const createEditStartedAtMsByIdRef = useRef<Record<string, number>>({});
  const activeMoodboardIdRef = useRef<string | null>(null);
  const [deletingMoodboardIds, setDeletingMoodboardIds] = useState<
    Record<string, boolean>
  >({});
  const [deletingImageIds, setDeletingImageIds] = useState<Record<string, boolean>>({});
  const [renamingMoodboardIds, setRenamingMoodboardIds] = useState<
    Record<string, boolean>
  >({});
  const renameReqSeqRef = useRef<Record<string, number>>({});

  const activeIsOptimistic = !!activeMoodboardId?.startsWith("optimistic-");
  const deletingActiveMoodboard = !!(
    activeMoodboardId && deletingMoodboardIds[activeMoodboardId]
  );

  // Track latest "Style" selection so async handlers can safely remap optimistic ids → real ids
  // even if the user changes selection after a request has started.
  const selectedStyleIdRef = useRef<string | null>(selectedMoodboardId);
  useEffect(() => {
    selectedStyleIdRef.current = selectedMoodboardId;
  }, [selectedMoodboardId]);

  useEffect(() => {
    activeMoodboardIdRef.current = activeMoodboardId;
  }, [activeMoodboardId]);

  // If the dialog is already open and the caller requests an active moodboard (create/edit),
  // honor it without reinitializing on list updates.
  const lastExplicitActiveIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      lastExplicitActiveIdRef.current = null;
      return;
    }
    if (!initialActiveMoodboardId) return;
    if (lastExplicitActiveIdRef.current === initialActiveMoodboardId) return;
    lastExplicitActiveIdRef.current = initialActiveMoodboardId;
    setActiveMoodboardId(initialActiveMoodboardId);
  }, [open, initialActiveMoodboardId]);

  const uploadingItems = useMemo(() => {
    if (!activeMoodboardId) return [];
    return uploadingItemsByMoodboardId[activeMoodboardId] || [];
  }, [activeMoodboardId, uploadingItemsByMoodboardId]);

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [searchOpen]);

  useEffect(() => {
    if (!open) return;
    // Don't force-exit edit mode when we're opening into an optimistic moodboard create flow.
    // (Create input "+ Create Moodboard" injects an optimistic item and expects immediate rename.)
    if (activeMoodboardId?.startsWith("optimistic-")) return;
    setEditingName(false);
    setNameDraft(activeMoodboard?.name || "");
  }, [open, activeMoodboardId, activeMoodboard?.name]);

  useEffect(() => {
    if (!editingName) return;
    setTimeout(() => {
      const el = nameInputRef.current;
      if (!el) return;
      el.focus();
      // Select all so users can quickly overwrite (requested UX)
      el.select();
    }, 0);
  }, [editingName]);

  const handleCreateNewMoodboard = useCallback(() => {
    setCreatingMoodboardCount((c) => c + 1);
    const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const now = new Date().toISOString();
    const optimistic: Moodboard = {
      id: tempId,
      name: DEFAULT_NEW_NAME,
      created_at: now,
      updated_at: now,
      files: [],
    };
    // Add immediately (optimistic), but DO NOT create on backend yet.
    createFallbackActiveIdByTempIdRef.current[tempId] = activeMoodboardId;
    onMoodboardsChange((prev) => [...prev, optimistic]);
    setActiveMoodboardId(tempId);
    setNameDraft(DEFAULT_NEW_NAME);
    setEditingName(true);
    createEditStartedAtMsByIdRef.current[tempId] = Date.now();
    // Scroll to the end so the new chip is visible
    setTimeout(() => {
      const el = chipsScrollRef.current;
      if (!el) return;
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    }, 0);
    setCreatingMoodboardCount((c) => Math.max(0, c - 1));
  }, [activeMoodboardId, onMoodboardsChange]);

  type CommitSource = "blur" | "enter";

  const commitName = useCallback(
    async (source: CommitSource) => {
      if (!activeMoodboardId) return;
      if (renamingMoodboardIds[activeMoodboardId]) return;
      const trimmed = nameDraft.trim();
      const isCreateFlow = activeMoodboardId.startsWith("optimistic-");
      const fallbackActiveId =
        createFallbackActiveIdByTempIdRef.current[activeMoodboardId] ??
        (selectedStyleIdRef.current &&
        !selectedStyleIdRef.current.startsWith("optimistic-")
          ? selectedStyleIdRef.current
          : null);

      // Radix focus juggling (dropdown -> dialog open) can cause an immediate blur which would
      // accidentally "confirm" the default name and call createMoodboard. Guard against this.
      if (isCreateFlow && source === "blur") {
        const startedAt = createEditStartedAtMsByIdRef.current[activeMoodboardId] || 0;
        if (startedAt && Date.now() - startedAt < 400) {
          // Keep editing; re-focus/select to make the UX obvious.
          setEditingName(true);
          setTimeout(() => nameInputRef.current?.focus(), 0);
          return;
        }
      }

      if (!trimmed) {
        // Empty name: if we were creating, cancel the temp moodboard entirely.
        if (isCreateFlow) {
          const tempId = activeMoodboardId;
          onMoodboardsChange((prev) => prev.filter((m) => m.id !== tempId));
          if (selectedStyleIdRef.current === tempId) onSelectMoodboardId(null);
          setEditingName(false);
          setNameDraft("");
          setActiveMoodboardId((prev) => (prev === tempId ? fallbackActiveId : prev));
          delete createFallbackActiveIdByTempIdRef.current[tempId];
          delete createEditStartedAtMsByIdRef.current[tempId];
          return;
        }
        setEditingName(false);
        setNameDraft(activeMoodboard?.name || "");
        return;
      }
      // For create flow, even the default name should create the moodboard when user confirms.
      if (!isCreateFlow && trimmed === activeMoodboard?.name) {
        setEditingName(false);
        setNameDraft(activeMoodboard?.name || "");
        return;
      }
      const moodboardId = activeMoodboardId;
      const prevName = activeMoodboard?.name || "";
      const reqId = isCreateFlow ? null : (renameReqSeqRef.current[moodboardId] || 0) + 1;
      try {
        if (isCreateFlow) {
          // Create on backend now that user has confirmed the name.
          setEditingName(false);
          onMoodboardsChange((prev) =>
            prev.map((m) => (m.id === moodboardId ? { ...m, name: trimmed } : m)),
          );
          const res = await createMoodboard(accountId, trimmed);
          // Replace optimistic moodboard with server response
          onMoodboardsChange((prev) =>
            prev.map((m) => (m.id === moodboardId ? res.data : m)),
          );
          // If this optimistic moodboard was selected for style, update style selection to real id
          if (selectedStyleIdRef.current === moodboardId)
            onSelectMoodboardId(res.data.id);
          // Only switch to the created moodboard if the user is still on it.
          setActiveMoodboardId((prev) => (prev === moodboardId ? res.data.id : prev));
          delete createFallbackActiveIdByTempIdRef.current[moodboardId];
          delete createEditStartedAtMsByIdRef.current[moodboardId];
          return;
        }

        renameReqSeqRef.current[moodboardId] = reqId ?? 0;
        setRenamingMoodboardIds((prev) => ({ ...prev, [moodboardId]: true }));
        // Exit edit mode immediately for a non-glitchy UI; update optimistically.
        setEditingName(false);
        onMoodboardsChange((prev) =>
          prev.map((m) => (m.id === moodboardId ? { ...m, name: trimmed } : m)),
        );
        const res = await updateMoodboardName(accountId, moodboardId, trimmed);
        // Ignore out-of-order responses
        if (renameReqSeqRef.current[moodboardId] !== reqId) return;
        onMoodboardsChange((prev) =>
          prev.map((m) => (m.id === res.data.id ? res.data : m)),
        );
      } catch (e) {
        console.error(e);
        if (isCreateFlow) {
          // Remove optimistic entry on failure
          onMoodboardsChange((prev) => prev.filter((m) => m.id !== moodboardId));
          if (selectedStyleIdRef.current === moodboardId) onSelectMoodboardId(null);
          // Only move the user away if they're still on the failing moodboard.
          setActiveMoodboardId((prev) =>
            prev === moodboardId ? fallbackActiveId : prev,
          );
          if (activeMoodboardIdRef.current === moodboardId) {
            setEditingName(false);
            setNameDraft("");
          }
          delete createFallbackActiveIdByTempIdRef.current[moodboardId];
          delete createEditStartedAtMsByIdRef.current[moodboardId];
        } else {
          // Ignore stale failures if a newer rename request exists
          if (!reqId || renameReqSeqRef.current[moodboardId] !== reqId) return;
          // Rollback optimistic update
          onMoodboardsChange((prev) =>
            prev.map((m) => (m.id === moodboardId ? { ...m, name: prevName } : m)),
          );
          setNameDraft(prevName);
        }
        if (shouldShowFallbackToast(e)) {
          toast.message(
            <ToastContent
              icon={LuTriangleAlert}
              headingPrefix="Network error"
              subheadingPrefix="Please check your connection and try again."
              isError={true}
            />,
          );
        }
      } finally {
        if (!isCreateFlow) {
          // Clear renaming flag only if this is still the latest request
          if (reqId && renameReqSeqRef.current[moodboardId] === reqId) {
            setRenamingMoodboardIds((prev) => {
              const next = { ...prev };
              delete next[moodboardId];
              return next;
            });
          }
        }
      }
    },
    [
      accountId,
      activeMoodboardId,
      nameDraft,
      activeMoodboard?.name,
      onMoodboardsChange,
      onSelectMoodboardId,
      renamingMoodboardIds,
    ],
  );

  const cancelCreateFlow = useCallback(
    (tempId: string) => {
      const fallbackActiveId =
        createFallbackActiveIdByTempIdRef.current[tempId] ??
        (selectedStyleIdRef.current &&
        !selectedStyleIdRef.current.startsWith("optimistic-")
          ? selectedStyleIdRef.current
          : null);
      onMoodboardsChange((prev) => prev.filter((x) => x.id !== tempId));
      if (selectedStyleIdRef.current === tempId) onSelectMoodboardId(null);
      setActiveMoodboardId((prev) => (prev === tempId ? fallbackActiveId : prev));
      if (activeMoodboardIdRef.current === tempId) {
        setNameDraft("");
        setEditingName(false);
      }
      delete createFallbackActiveIdByTempIdRef.current[tempId];
      delete createEditStartedAtMsByIdRef.current[tempId];
    },
    [onMoodboardsChange, onSelectMoodboardId],
  );

  // If the dialog is opened with an optimistic moodboard (e.g. "Create Moodboard" from dropdown),
  // automatically enter edit mode once, with all text selected.
  useEffect(() => {
    if (!open) {
      didAutoStartEditForOptimisticIdRef.current = {};
      return;
    }
    if (!activeMoodboardId) return;
    if (!activeMoodboardId.startsWith("optimistic-")) return;
    if (didAutoStartEditForOptimisticIdRef.current[activeMoodboardId]) return;
    didAutoStartEditForOptimisticIdRef.current[activeMoodboardId] = true;
    setNameDraft(activeMoodboard?.name || DEFAULT_NEW_NAME);
    setEditingName(true);
    createEditStartedAtMsByIdRef.current[activeMoodboardId] = Date.now();
    // Ensure a stable fallback for this optimistic moodboard even if other creates start later.
    if (!(activeMoodboardId in createFallbackActiveIdByTempIdRef.current)) {
      const firstRealId =
        moodboards.find((m) => !m.id.startsWith("optimistic-"))?.id ?? null;
      createFallbackActiveIdByTempIdRef.current[activeMoodboardId] =
        selectedStyleIdRef.current &&
        !selectedStyleIdRef.current.startsWith("optimistic-")
          ? selectedStyleIdRef.current
          : firstRealId;
    }
  }, [open, activeMoodboardId, activeMoodboard?.name, moodboards]);

  const handleUploadClick = () => {
    if (!activeMoodboardId) return;
    uploadTargetMoodboardIdRef.current = activeMoodboardId;
    fileInputRef.current?.click();
  };

  const handleUploadFiles = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const moodboardId = uploadTargetMoodboardIdRef.current ?? activeMoodboardId;
      if (!moodboardId) return;
      // Guard against optimistic ids (backend doesn't know them)
      if (moodboardId.startsWith("optimistic-")) {
        if (e.target) e.target.value = "";
        return;
      }
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      // Create immediate preview items with temp IDs (library-like)
      const newUploadingItems: UploadingItem[] = files.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const preview = URL.createObjectURL(file);
        return { id: tempId, file, preview };
      });
      setUploadingItemsByMoodboardId((prev) => ({
        ...prev,
        [moodboardId]: [...newUploadingItems, ...(prev[moodboardId] || [])],
      }));

      setUploading(true);
      try {
        const res = await addImagesToMoodboard(accountId, moodboardId, files);
        const newFiles = res.files || [];

        // Update moodboard in-place with response files (no refetch)
        onMoodboardsChange((prev) =>
          prev.map((m) => {
            if (m.id !== moodboardId) return m;
            const existing = m.files || [];
            const byId = new Map<string, MoodboardFile>();
            [...newFiles, ...existing].forEach((f) => {
              if (!byId.has(f.id)) byId.set(f.id, f);
            });
            return { ...m, files: Array.from(byId.values()) };
          }),
        );
      } catch (err) {
        console.error(err);
        if (shouldShowFallbackToast(err)) {
          toast.message(
            <ToastContent
              icon={LuTriangleAlert}
              headingPrefix="Network error"
              subheadingPrefix="Please check your connection and try again."
              isError={true}
            />,
          );
        }
        // Clean up failed upload previews
        newUploadingItems.forEach((item) => {
          if (item.preview.startsWith("blob:")) URL.revokeObjectURL(item.preview);
        });
      } finally {
        setUploading(false);
        // Remove upload placeholders + cleanup blob URLs
        setUploadingItemsByMoodboardId((prev) => {
          const removeIds = new Set(newUploadingItems.map((i) => i.id));
          const list = prev[moodboardId] || [];
          list.forEach((i) => {
            if (removeIds.has(i.id) && i.preview.startsWith("blob:")) {
              URL.revokeObjectURL(i.preview);
            }
          });
          const nextList = list.filter((i) => !removeIds.has(i.id));
          return { ...prev, [moodboardId]: nextList };
        });
        if (e.target) e.target.value = "";
      }
    },
    [accountId, activeMoodboardId, onMoodboardsChange],
  );

  const handleDeleteMoodboard = useCallback(async () => {
    if (!activeMoodboardId) return;
    const deletingId = activeMoodboardId;
    if (deletingMoodboardIds[deletingId]) return;

    const idx = moodboards.findIndex((m) => m.id === deletingId);
    const item = idx >= 0 ? moodboards[idx] : null;
    const remaining = moodboards.filter((m) => m.id !== deletingId);
    // Pick the previous moodboard if available; otherwise the next; otherwise none
    const nextId = remaining[idx - 1]?.id ?? remaining[idx]?.id ?? null;
    const didUpdateStyleSelection = selectedMoodboardId === deletingId;

    setDeletingMoodboardIds((prev) => ({ ...prev, [deletingId]: true }));
    try {
      // Optimistically remove from UI immediately (rollback on failure)
      onMoodboardsChange((prev) => prev.filter((m) => m.id !== deletingId));
      setActiveMoodboardId(nextId);
      setEditingName(false);
      setNameDraft("");
      if (didUpdateStyleSelection) {
        onSelectMoodboardId(nextId);
      }

      await deleteMoodboard(accountId, deletingId);
    } catch (err) {
      console.error(err);
      // Rollback optimistic removal
      if (item) {
        onMoodboardsChange((prev) => {
          // If it was already restored somehow, don't duplicate
          if (prev.some((m) => m.id === item.id)) return prev;
          const next = [...prev];
          // Restore at original position when possible
          const insertAt = Math.min(Math.max(idx, 0), next.length);
          next.splice(insertAt, 0, item);
          return next;
        });
        setActiveMoodboardId(item.id);
        setNameDraft(item.name);
      }
      if (didUpdateStyleSelection) {
        onSelectMoodboardId(deletingId);
      }
      if (shouldShowFallbackToast(err)) {
        toast.message(
          <ToastContent
            icon={LuTriangleAlert}
            headingPrefix="Network error"
            subheadingPrefix="Please check your connection and try again."
            isError={true}
          />,
        );
      }
    } finally {
      setDeletingMoodboardIds((prev) => {
        const next = { ...prev };
        delete next[deletingId];
        return next;
      });
    }
  }, [
    accountId,
    activeMoodboardId,
    deletingMoodboardIds,
    moodboards,
    onMoodboardsChange,
    onSelectMoodboardId,
    selectedMoodboardId,
  ]);

  const handleRemoveImage = useCallback(
    async (fileId: string) => {
      if (!activeMoodboardId) return;
      if (deletingImageIds[fileId]) return;
      setDeletingImageIds((prev) => ({ ...prev, [fileId]: true }));
      // Optimistically remove the image from UI immediately (rollback on failure)
      const fileToRestore =
        (activeMoodboard?.files || []).find((f) => f.id === fileId) || null;
      onMoodboardsChange((prev) =>
        prev.map((m) =>
          m.id === activeMoodboardId
            ? { ...m, files: (m.files || []).filter((f) => f.id !== fileId) }
            : m,
        ),
      );
      try {
        await removeImageFromMoodboard(accountId, activeMoodboardId, fileId);
      } catch (err) {
        console.error(err);
        // Rollback optimistic removal
        if (fileToRestore) {
          onMoodboardsChange((prev) =>
            prev.map((m) => {
              if (m.id !== activeMoodboardId) return m;
              const current = m.files || [];
              // If it already exists (e.g. due to other updates), don't duplicate
              if (current.some((f) => f.id === fileToRestore.id)) return m;
              return { ...m, files: [fileToRestore, ...current] };
            }),
          );
        }
        if (shouldShowFallbackToast(err)) {
          toast.message(
            <ToastContent
              icon={LuTriangleAlert}
              headingPrefix="Network error"
              subheadingPrefix="Please check your connection and try again."
              isError={true}
            />,
          );
        }
      } finally {
        setDeletingImageIds((prev) => {
          const next = { ...prev };
          delete next[fileId];
          return next;
        });
      }
    },
    [accountId, activeMoodboardId, onMoodboardsChange, deletingImageIds, activeMoodboard],
  );

  const files: MoodboardFile[] = activeMoodboard?.files || [];
  const filteredMoodboards = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return moodboards;
    return moodboards.filter((m) => (m.name || "").toLowerCase().includes(term));
  }, [moodboards, searchTerm]);

  // Keep the active moodboard chip visible in the horizontal list.
  useEffect(() => {
    if (!open) return;
    if (!activeMoodboardId) return;
    const el = chipRefs.current[activeMoodboardId];
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    });
  }, [open, activeMoodboardId, filteredMoodboards.length]);

  // Track horizontal scroll state for fade gradients (same behavior as competitor brand chips)
  useEffect(() => {
    const el = chipsScrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };
    update();
    const onScroll = () => update();
    el.addEventListener("scroll", onScroll, { passive: true } as AddEventListenerOptions);
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [filteredMoodboards.length, searchOpen]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          "p-[6px] bg-white border border-[#E7E8E9] shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-[12px]",
          "w-[828px] max-w-[95vw] h-[717px] max-h-[95vh]",
          "flex flex-col gap-[16px] items-start overflow-x-hidden",
        )}
      >
        <DialogHeader className="w-full">
          {/* Top bar */}
          <div className="w-full flex items-center gap-2">
            {/* Search (same pattern as competitor brand chips) */}
            <div className="flex items-center gap-2 min-w-0">
              {searchOpen ? (
                <div
                  className="bg-white h-7 pl-2 pr-2 py-1 flex items-center gap-[6px] rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray"
                  style={{ minWidth: 240 }}
                >
                  <RiSearchLine size={14} className="text-brand-primary-400 shrink-0" />
                  <input
                    ref={searchInputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search moodboards"
                    className="outline-none border-0 ring-0 text-[11px] placeholder:text-[11px] leading-[18px] flex-1 text-brand-primary-800 placeholder:text-brand-primary-400 bg-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setSearchOpen(false);
                    }}
                    className="p-0.5"
                    aria-label="Clear search and close"
                  >
                    <RiCloseLine size={14} className="text-brand-primary-400" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  disabled={!moodboards || moodboards.length === 0}
                  className={cn(
                    "bg-white h-7 w-7 shrink-0 p-0 flex items-center justify-center rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray",
                    (!moodboards || moodboards.length === 0) &&
                      "opacity-50 cursor-not-allowed",
                  )}
                  aria-label="Search moodboards"
                >
                  <RiSearchLine size={14} className="text-[#9AA3BD]" />
                </button>
              )}

              {/* Moodboards horizontally (like competitor brands chips) */}
              <div className="relative overflow-hidden min-w-0">
                <div
                  ref={chipsScrollRef}
                  className="overflow-x-auto flex items-center gap-2 scrollbar-hide"
                >
                  {filteredMoodboards.map((m) => {
                    const isSelected = m.id === activeMoodboardId;
                    return (
                      <button
                        key={m.id}
                        ref={(el) => {
                          chipRefs.current[m.id] = el;
                        }}
                        type="button"
                        onClick={() => {
                          // While editing, clicking the chip shouldn't exit edit mode or reset the draft.
                          if (isSelected && editingName) return;
                          setActiveMoodboardId(m.id);
                          setEditingName(false);
                          setNameDraft(m.name);
                        }}
                        onDoubleClick={() => {
                          setActiveMoodboardId(m.id);
                          setNameDraft(m.name);
                          setEditingName(true);
                        }}
                        className={cn(
                          "group shrink-0 h-[28px] max-w-[153px] px-[8px] py-[4px] flex items-center gap-[6px] rounded-[6px] transition-colors whitespace-nowrap",
                          isSelected
                            ? "bg-[#FFF9F7] border border-[#FF4500] shadow-[0px_1px_2px_rgba(0,0,0,0.05)]"
                            : "bg-white squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray",
                        )}
                        title={m.name}
                      >
                        {/* Text + pencil share one measured region so edit input can use the pencil space */}
                        <div className="relative flex items-center min-w-0 w-fit max-w-[137px] h-[20px]">
                          {isSelected && editingName ? (
                            <>
                              {/* Invisible sizer keeps width identical between view/edit (includes icon region) */}
                              <span
                                aria-hidden="true"
                                className={cn(
                                  "invisible inline-block h-[20px]",
                                  // Keep the text width consistent with view mode (117px max)...
                                  "max-w-[117px] overflow-hidden text-ellipsis whitespace-pre",
                                  "text-[12px] leading-[19px] tracking-[0.02em] font-medium font-['Helvetica Neue']",
                                )}
                              >
                                {(nameDraft || m.name || " ").replace(/ /g, "\u00A0")}
                              </span>
                              {/* ...and reserve the pencil-icon region so width stays identical when entering edit */}
                              <div
                                aria-hidden="true"
                                className="ml-[6px] shrink-0 w-[14px] h-[14px]"
                              />
                              <input
                                ref={nameInputRef}
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onFocus={(e) => e.currentTarget.select()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={() => void commitName("blur")}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    void commitName("enter");
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    // Escape cancels rename; for create flow, it cancels the temp moodboard entirely.
                                    if (m.id.startsWith("optimistic-")) {
                                      cancelCreateFlow(m.id);
                                    } else {
                                      setEditingName(false);
                                      setNameDraft(m.name);
                                    }
                                  }
                                  e.stopPropagation();
                                }}
                                className={cn(
                                  "absolute inset-0 w-full h-[20px] bg-transparent outline-none text-left p-0 m-0 block",
                                  "text-[12px] leading-[19px] tracking-[0.02em] font-medium font-['Helvetica Neue']",
                                  "text-[rgba(2,24,89,0.8)]",
                                )}
                              />
                            </>
                          ) : (
                            <>
                              <div className="min-w-0 max-w-[117px] h-[20px] flex items-center">
                                <span
                                  className={cn(
                                    "truncate block w-full text-left",
                                    isSelected
                                      ? "text-[12px] leading-[19px] tracking-[0.02em] font-medium font-['Helvetica Neue'] text-[rgba(2,24,89,0.8)]"
                                      : "text-[12px] tracking-[0.02em] font-normal font-['Helvetica Neue'] text-brand-primary-800",
                                  )}
                                >
                                  {m.name}
                                </span>
                              </div>
                              {isSelected &&
                                (m.id.startsWith("optimistic-") ? (
                                  <div className="ml-[6px] shrink-0 w-[14px] h-[14px] flex items-center justify-center">
                                    <LuLoaderCircle
                                      size={14}
                                      className="text-[#9AA3BD] animate-spin"
                                    />
                                  </div>
                                ) : renamingMoodboardIds[m.id] ? (
                                  <div className="ml-[6px] shrink-0 w-[14px] h-[14px] flex items-center justify-center">
                                    <LuLoaderCircle
                                      size={14}
                                      className="text-[#9AA3BD] animate-spin"
                                    />
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="ml-[6px] opacity-100 transition-opacity shrink-0 w-[14px] h-[14px] flex items-center justify-center"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setNameDraft(m.name);
                                      setEditingName(true);
                                    }}
                                    aria-label="Edit moodboard name"
                                  >
                                    <RiPencilLine size={14} className="text-[#9AA3BD]" />
                                  </button>
                                ))}
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {canScrollLeft && (
                  <div
                    className="absolute left-0 top-0 bottom-0 w-[30px] h-[28px] z-10 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(90deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
                    }}
                  />
                )}
                {canScrollRight && (
                  <div
                    className="absolute right-0 top-0 bottom-0 w-[30px] h-[28px] z-10 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(270deg, #FFF 6.25%, rgba(255, 255, 255, 0.00) 100%)",
                    }}
                  />
                )}
              </div>
            </div>

            {/* Floating Add Moodboard button (pinned right, like competitor brand chips) */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => void handleCreateNewMoodboard()}
                className="bg-white h-7 w-7 shrink-0 p-0 flex items-center justify-center rounded-[6px] squircle squircle-radius-[6px] squircle-smooth-[1] squircle-outline-[1px] squircle-fill-border-gray text-[11px] text-brand-primary-800 leading-[19px] tracking-[0.02em] transition-colors"
                aria-label="Add moodboard"
              >
                <RiAddLargeLine size={14} className="text-brand-primary-400" />
              </button>
            </div>
          </div>
        </DialogHeader>

        {/* Images area */}
        <div className="w-full flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="w-full flex-1 overflow-y-auto overflow-x-hidden min-h-0 scrollbar-hide">
            {files.length === 0 && uploadingItems.length === 0 ? (
              <div className="min-h-full w-full flex flex-col items-center justify-center">
                <img src={EmptySVG} alt="Empty moodboard" />
                <h6 className="font-medium text-[13px] leading-[100%] text-center text-brand-primary-800 relative -top-[32px]">
                  Nothing here yet.
                </h6>
                <p className="text-brand-primary-400 font-normal text-[12px] leading-[100%] tracking-[0] text-center relative -top-[26px]">
                  Upload JPEG/PNG files to get started.
                </p>
              </div>
            ) : (
              <div className="w-full flex flex-wrap justify-start content-start items-start gap-[8px] p-0">
                {/* Uploading previews (library-like) */}
                {uploadingItems.map((item) => (
                  <div key={item.id} className="relative w-[156px] h-[156px]">
                    <img
                      src={item.preview}
                      alt={item.file.name}
                      className="w-[156px] h-[156px] object-cover rounded-[3.2px]"
                      draggable={false}
                    />
                    <div className="absolute inset-0 bg-white/40 grid place-items-center backdrop-blur-[4px] rounded-[3.2px]">
                      <Lottie
                        animationData={uploadingLoader}
                        loop
                        autoplay
                        style={{ transform: "scale(1.8)" }}
                      />
                    </div>
                  </div>
                ))}
                {files.map((f) => {
                  const isDeleting = !!deletingImageIds[f.id];
                  return (
                    <div key={f.id} className="group relative w-[156px] h-[156px]">
                      <img
                        src={f.public_url}
                        alt={f.file_name}
                        className="w-[156px] h-[156px] object-cover rounded-[3.2px]"
                      />
                      <button
                        type="button"
                        disabled={isDeleting}
                        className={cn(
                          "absolute top-2 right-2 transition-opacity bg-white/90 border border-[#E7E8E9] rounded-[8px] p-1",
                          isDeleting
                            ? "opacity-100 cursor-not-allowed"
                            : "opacity-0 group-hover:opacity-100",
                        )}
                        onClick={() => void handleRemoveImage(f.id)}
                        aria-label="Remove image"
                        title="Remove"
                      >
                        {isDeleting ? (
                          <LuLoaderCircle
                            size={14}
                            className="text-[#9AA3BD] animate-spin"
                          />
                        ) : (
                          <RiDeleteBinLine size={14} className="text-[#FF3333]" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer buttons */}
          <div className="w-full p-[6px] bg-white">
            <div className="w-full flex items-center justify-between">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void handleUploadFiles(e)}
                />
                <Button
                  disabled={
                    !activeMoodboardId ||
                    activeIsOptimistic ||
                    uploading ||
                    deletingActiveMoodboard
                  }
                  onClick={handleUploadClick}
                  className="h-[32px] px-[12px] py-[6px] rounded-[8px] bg-[#FF6A33] hover:bg-[#FF6A33]/90 text-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)]"
                >
                  <RiUploadLine size={14} className="text-white" />
                  <span className="ml-[6px] text-[12px] leading-[19px] tracking-[0.02em] font-medium font-['Helvetica Neue'] text-white">
                    Upload Images
                  </span>
                </Button>
              </div>

              <Button
                variant="outline"
                disabled={
                  !activeMoodboardId || activeIsOptimistic || deletingActiveMoodboard
                }
                onClick={() => void handleDeleteMoodboard()}
                className="h-[32px] px-[12px] py-[6px] rounded-[8px] bg-white border border-[#E7E8E9] shadow-[0px_1px_2px_rgba(0,0,0,0.05)]"
              >
                {deletingActiveMoodboard ? (
                  <LuLoaderCircle size={16} className="text-[#9AA3BD] animate-spin" />
                ) : (
                  <RiDeleteBinLine size={14} className="text-[#FF3333]" />
                )}
                <span className="ml-[6px] text-[12px] leading-[19px] tracking-[0.02em] font-medium font-['Helvetica Neue'] text-[rgba(2,24,89,0.8)]">
                  Delete Moodboard
                </span>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoodboardManagerDialog;
